import gettext, { ngettext } from '@sdk/lang/gettext';
import { useToggle } from '@sdk/common/hooks/useToggle';
import Button from '@sdk/components/ui/buttons/Button';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import ToastComponent from '@sdk/common/toast/ToastComponent';

import DeleteIcon from '@web/assets/svg/UI/person-delete.svg';
import { EmptyAnnouncementsList } from '@web/announcements/EmptyAnnouncementsList/EmptyAnnouncementsList';
import { AnnouncementItem } from '@web/announcements/AnnouncementItem/AnnouncementItem';
import { AnnouncementDetails } from '@web/announcements/AnnouncementDetails/AnnouncementDetails';
import { AnnouncementViews } from '@web/announcements/AnnouncementViews/AnnouncementViews';
import { DeleteAnnouncementConfirm } from '@web/announcements/DeleteAnnouncementConfirm/DeleteAnnouncementConfirm';
import { DeleteMultipleAnnouncementsConfirm } from '@web/announcements/DeleteMultipleAnnouncementsConfirm/DeleteMultipleAnnouncementsConfirm';
import { NewAnnouncementForm } from '@web/announcements/NewAnnouncementForm/NewAnnouncementForm';
import { useAnnouncementsUtils } from '@web/announcements/hooks/useAnnouncementUtils';

import { HeaderSkeleton } from './components/HeaderSkeleton';
import { ListItemSkeleton } from './components/ListItemSkeleton';
import { LoadMore } from './components/LoadMore/LoadMore';
import { useAnnouncementItems, useBulkSelectId, useSelectId } from './announcementsList.hooks';
import {
  Header,
  HeaderAction,
  HeaderTitle,
  List,
  ListItem,
  RibbonMenu,
  RibbonMenuButton,
  RibbonMenuItem,
  SelectionCounter,
  Wrapper,
} from './announcementsList.styles';

export const AnnouncementsList = () => {
  const { isSender } = useAnnouncementsUtils();
  const { hasEmptyData, isLoading, items, itemsCount } = useAnnouncementItems();
  const {
    cancelBulkSelection,
    clearSelection,
    confirmBulkSelection,
    hasSelection,
    isBulkSelectionConfirmed,
    selectedIds,
    toggleSelection,
  } = useBulkSelectId();

  const {
    unselect: closeDetailsModal,
    isSelected: isDetailsModalOn,
    select: openDetailsModal,
    selectedId: selectedDetailsId,
  } = useSelectId();
  const {
    unselect: closeDeleteModal,
    isSelected: isDeleteModalOn,
    select: openDeleteModal,
    selectedId: selectedDeleteId,
  } = useSelectId();
  const {
    unselect: closeViewsModal,
    isSelected: isViewsModalOn,
    select: openViewsModal,
    selectedId: selectedViewsId,
  } = useSelectId();
  const { isOn, setOff, setOn } = useToggle(false);

  const title = ngettext('Announcement', 'Announcements', itemsCount);

  return (
    <>
      <Wrapper>
        <Header>
          {isLoading ? (
            <HeaderSkeleton />
          ) : (
            <HeaderTitle as="h2">
              {itemsCount} {title}
            </HeaderTitle>
          )}

          {hasSelection && (
            <SelectionCounter>
              {selectedIds.size} {gettext('selected')}
            </SelectionCounter>
          )}

          {isSender && (
            <HeaderAction>
              <Button buttonType={ButtonType.primary} onClick={setOn}>
                {gettext('Send announcement')}
              </Button>
            </HeaderAction>
          )}
        </Header>

        {hasSelection && (
          <RibbonMenu>
            <RibbonMenuItem>
              <RibbonMenuButton onClick={() => confirmBulkSelection()}>
                <DeleteIcon />
                {gettext('Delete')}
              </RibbonMenuButton>
            </RibbonMenuItem>
          </RibbonMenu>
        )}

        {hasEmptyData ? (
          <EmptyAnnouncementsList alternative={isSender} />
        ) : (
          <>
            <List>
              {isLoading ? (
                <>
                  <ListItem>
                    <ListItemSkeleton />
                  </ListItem>
                  <ListItem>
                    <ListItemSkeleton />
                  </ListItem>
                  <ListItem>
                    <ListItemSkeleton />
                  </ListItem>
                </>
              ) : (
                items.map((item) => (
                  <ListItem key={item.id}>
                    <AnnouncementItem
                      isSelected={selectedIds.has(item.id)}
                      item={item}
                      onClick={openDetailsModal}
                      onDelete={openDeleteModal}
                      onSelect={toggleSelection}
                      onShowViews={openViewsModal}
                    />
                  </ListItem>
                ))
              )}
            </List>
            <LoadMore />
          </>
        )}
      </Wrapper>

      {isOn && <NewAnnouncementForm onClose={setOff} />}
      {isDetailsModalOn && (
        <AnnouncementDetails
          id={selectedDetailsId}
          onClose={closeDetailsModal}
          onDelete={openDeleteModal}
        />
      )}
      {isViewsModalOn && <AnnouncementViews id={selectedViewsId} onClose={closeViewsModal} />}
      {isDeleteModalOn && (
        <DeleteAnnouncementConfirm
          id={selectedDeleteId}
          onClose={closeDeleteModal}
          onDelete={() => {
            toggleSelection(selectedDeleteId, true);
            closeDetailsModal();
          }}
        />
      )}

      {isBulkSelectionConfirmed && (
        <DeleteMultipleAnnouncementsConfirm
          ids={Array.from(selectedIds)}
          onClose={cancelBulkSelection}
          onDelete={clearSelection}
        />
      )}

      <ToastComponent />
    </>
  );
};
